import React from "react";
import { CheckCircle2, Star, Users, Sparkles, Crown, ShoppingCart, Package, BarChart3, RotateCcw, Store, ClipboardList } from "lucide-react";
import { Button } from "./Button";
import { landingContent } from "./i18n";

// Ablefy-Zahlungslinks (Platzhalter – werden von Petra nachgeliefert)
const COM_CHECKOUT = {
  starter: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-commerce-studio-pro-ab36d135/payment",
  pro: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-commerce-studio-business-c099188e/payment",
  business: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-project-workforce-studio-business-378a6e30/payment",
};

// Sprachunabhängige Metadaten
const COM_META = {
  starter: { icon: Users, price: "49", highlight: false },
  pro: { icon: Sparkles, price: "79", highlight: true },
  business: { icon: Crown, price: "119", highlight: false },
};

const FEATURE_ICONS = [ShoppingCart, Package, BarChart3, ClipboardList, RotateCcw, Store];

const CommerceSection = ({ onLogin, lang = "de" }) => {
  const t = landingContent[lang].commerce;
  const goCom = (url) => { if (url) window.open(url, "_blank", "noopener,noreferrer"); else if (onLogin) onLogin(); };

  const plans = [
    { key: "starter", name: "Starter", per: " /Monat", tagline: t.highlights[0].text, featsTitle: "Enthalten:", feats: t.compTable.filter(r => r.s).map(r => r.f) },
    { key: "pro", name: "Pro", per: " /Monat", tagline: t.highlights[4].text, featsTitle: "Enthalten:", feats: t.compTable.filter(r => r.p).map(r => r.f) },
    { key: "business", name: "Business", per: " /Monat", tagline: t.highlights[5].text, featsTitle: "Alle Funktionen:", feats: t.compTable.filter(r => r.b).map(r => r.f) },
  ];

  return (
    <section className="px-6 lg:px-12 py-16" style={{ background: "var(--brand-border-light)" }} data-testid="landing-commerce">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.eyebrow}</span></div>
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-2">{t.heading}</h2>
        <p className="text-center text-[var(--brand-ink)] font-medium mb-3">{t.sub}</p>
        <p className="text-center text-[var(--brand-muted)] mb-10 max-w-2xl mx-auto">{t.lead}</p>

        {/* Feature-Highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {t.highlights.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || ShoppingCart;
            return (
              <div key={f.title} className="p-5 rounded-xl border border-[var(--brand-border)] bg-white hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: "var(--brand-border-light)" }}>
                  <Icon className="w-5 h-5 text-[var(--brand-primary)]" />
                </div>
                <h3 className="font-semibold text-[var(--brand-ink)] mb-1">{f.title}</h3>
                <p className="text-sm text-[var(--brand-muted)]">{f.text}</p>
              </div>
            );
          })}
        </div>

        {/* Preise */}
        <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="com-price-glance">
          {plans.map((p) => {
            const m = COM_META[p.key];
            return (
              <div key={p.key} className={`flex items-baseline gap-2 rounded-full border px-5 py-2.5 ${m.highlight ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white" : "bg-white border-[var(--brand-border)] text-[var(--brand-ink)]"}`}>
                <span className={`text-xs uppercase tracking-wider font-semibold ${m.highlight ? "text-white/80" : "text-[var(--brand-muted)]"}`}>{p.name}</span>
                <span className="text-lg font-bold">{m.price} €</span>
                <span className={`text-xs ${m.highlight ? "text-white/70" : "text-[var(--brand-muted)]"}`}>{p.per}</span>
              </div>
            );
          })}
        </div>

        <div className="space-y-6">
          {plans.map((p) => {
            const m = COM_META[p.key];
            const Icon = m.icon;
            return (
              <div key={p.key} className={`relative rounded-2xl overflow-hidden border grid grid-cols-1 md:grid-cols-5 bg-white ${m.highlight ? "border-[var(--brand-primary)] shadow-xl" : "border-[var(--brand-border)]"}`} data-testid={`com-plan-${p.key}`}>
                {m.highlight && (<span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 bg-white text-[var(--brand-ink)] text-xs px-3 py-1 rounded-full shadow"><Star className="w-3.5 h-3.5 fill-[var(--brand-primary)] text-[var(--brand-primary)]" /> Beliebt</span>)}
                <div className="md:col-span-3 p-8 lg:p-10">
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold mb-2"><Icon className="w-4 h-4" /> {p.name}</span>
                  <h4 className="font-[Playfair_Display] text-2xl font-bold text-[var(--brand-ink)] mb-2">Commerce Studio {p.name}</h4>
                  <p className="text-sm text-[var(--brand-muted)] mb-5">{p.tagline}</p>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)] mb-3">{p.featsTitle}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {p.feats.map((ft) => (<li key={ft} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {ft}</li>))}
                  </ul>
                </div>
                <div className="md:col-span-2 p-8 lg:p-10 flex flex-col justify-center text-white" style={{ background: m.highlight ? "linear-gradient(160deg, #5B636C 0%, #3E454C 100%)" : "linear-gradient(160deg, #7A828B 0%, #565E66 100%)" }}>
                  <p className="text-xs uppercase tracking-wider text-white/70">Monatliche Miete</p>
                  <div className="my-2"><span className="text-5xl font-bold">{m.price} €</span><span className="text-white/75">{p.per}</span></div>
                  <Button onClick={() => goCom(COM_CHECKOUT[p.key])} className="w-full rounded-full bg-white text-[var(--brand-ink)] hover:bg-white/90 font-semibold" data-testid={`com-start-${p.key}`}>{COM_CHECKOUT[p.key] ? "Jetzt kaufen" : "Jetzt kaufen"}</Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Vergleichstabelle */}
        <div className="mt-12" data-testid="com-compare">
          <h3 className="font-[Playfair_Display] text-2xl font-bold text-center text-[var(--brand-ink)] mb-6">Funktionsvergleich</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[var(--brand-border)]">
                  <th className="text-left py-3 px-4 font-semibold text-[var(--brand-ink)]">Funktion</th>
                  <th className="text-center py-3 px-4 font-semibold text-[var(--brand-ink)]">Starter</th>
                  <th className="text-center py-3 px-4 font-semibold text-[var(--brand-primary)]">Pro</th>
                  <th className="text-center py-3 px-4 font-semibold text-[var(--brand-ink)]">Business</th>
                </tr>
              </thead>
              <tbody>
                {t.compTable.map((row) => (
                  <tr key={row.f} className="border-b border-[var(--brand-border)]/50">
                    <td className="py-2.5 px-4 text-[var(--brand-ink)]">{row.f}</td>
                    <td className="text-center py-2.5 px-4">{row.s ? <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-[var(--brand-muted)]">&ndash;</span>}</td>
                    <td className="text-center py-2.5 px-4">{row.p ? <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-[var(--brand-muted)]">&ndash;</span>}</td>
                    <td className="text-center py-2.5 px-4">{row.b ? <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-[var(--brand-muted)]">&ndash;</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Zielgruppen */}
        <div className="mt-12 rounded-2xl border border-[var(--brand-border)] bg-white p-6 lg:p-8" data-testid="com-audience">
          <h3 className="font-[Playfair_Display] text-xl font-bold text-[var(--brand-ink)] mb-4">Ideal geeignet für</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {t.audience.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {item}</li>
            ))}
          </ul>
        </div>

        <p className="text-center text-xs text-[var(--brand-muted)] mt-8 max-w-2xl mx-auto">{t.servicesNote}</p>
      </div>
    </section>
  );
};

export default CommerceSection;
